// generated by the plop

import { Injectable } from '@angular/core';
import { Selector, State } from '@ngxs/store';
import {
  AccessState,
  AccessStateModel,
  ORDERS_ACCESS_DEFAULT,
  OrdersAccessModel,
  ProfileState,
  ProfileStateModel,
  UserModel
} from 'shared';
import { PermissionEnum } from 'symbols';

@State({ name: 'OrdersAccess' })
@Injectable()
export class OrdersAccessState {

  /**
   * Generate configs for classroom module depends on a provided user role.
   */
  static generateOrdersAccess(roles: PermissionEnum[], user: UserModel): OrdersAccessModel {
    // Return permissions as for an unknown role if user not verified or not authenticated.
    if (!user) {
      return this.generateRoleConfig(PermissionEnum.UNKNOWN);
    }

    // Apply permissions of each provided role.
    const rolesConfig: OrdersAccessModel[] = roles.map(role => this.generateRoleConfig(role));

    // Return defined config.
    return this.mergeConfig(rolesConfig);
  }

  /**
   * Define and create config using provided config data.
   */
  static generateRoleConfig(role: PermissionEnum): OrdersAccessModel {
    switch (role) {
      case PermissionEnum.CAN_READ_ORDERS:
        return {
          ...ORDERS_ACCESS_DEFAULT,
          canReadOrders: true
        };
      case PermissionEnum.CAN_CHANGE_ORDER_STATUS:
        return {
          ...ORDERS_ACCESS_DEFAULT,
          canChangeOrderStatus: true
        };
      case PermissionEnum.CAN_READ_ORDERS_BY_ID:
        return {
          ...ORDERS_ACCESS_DEFAULT,
          canReadOrdersById: true
        };
      case PermissionEnum.CAN_WRITE_ORDERS:
        return {
          ...ORDERS_ACCESS_DEFAULT,
          canWriteOrders: true
        };
      case PermissionEnum.CAN_READ_EQUIPMENT_OF_ORDER:
        return {
          ...ORDERS_ACCESS_DEFAULT,
          canReadEquipmentOfOrder: true
        };
      case PermissionEnum.CAN_READ_RECEIPT_OF_ORDER:
        return {
          ...ORDERS_ACCESS_DEFAULT,
          canReadReceiptOfOrder: true
        };
      case PermissionEnum.CAN_WRITE_RECEIPT_OF_ORDER:
        return {
          ...ORDERS_ACCESS_DEFAULT,
          canWriteReceiptOfOrder: true
        };
      case PermissionEnum.CAN_SAVE_FORCE:
        return {
          ...ORDERS_ACCESS_DEFAULT,
          canSaveForce: true
        };

      case PermissionEnum.CAN_READ_DEBTS:
        return {
          ...ORDERS_ACCESS_DEFAULT,
          canReadDebts: true
        };
        case PermissionEnum.CAN_WRITE_DEBTS:
        return {
          ...ORDERS_ACCESS_DEFAULT,
          canWriteDebts: true
        };

      case PermissionEnum.SUPER_USER:
        return {
          canReadOrdersById: true,
          canReadOrders: true,
          canWriteOrders: true,
          canChangeOrderStatus: true,
          canReadEquipmentOfOrder: true,
          canReadReceiptOfOrder: true,
          canWriteReceiptOfOrder: true,
          canReadDebts: true,
          canSaveForce: true,
          canWriteDebts: true,
        };
      default: {
        return {
          ...ORDERS_ACCESS_DEFAULT
        };
      }
    }
  }

  /**
   * Merge provided roles configs (by union rule).
   */
  static mergeConfig(rolesConfig: OrdersAccessModel[]): OrdersAccessModel {
    const moduleConfig: OrdersAccessModel = ORDERS_ACCESS_DEFAULT;

    for (const config of rolesConfig) {
      moduleConfig.canReadOrders = moduleConfig.canReadOrders ? true : config.canReadOrders;
      moduleConfig.canReadOrdersById = moduleConfig.canReadOrdersById ? true : config.canReadOrdersById;
      moduleConfig.canReadEquipmentOfOrder = moduleConfig.canReadEquipmentOfOrder ? true : config.canReadEquipmentOfOrder;
      moduleConfig.canChangeOrderStatus = moduleConfig.canChangeOrderStatus ? true : config.canChangeOrderStatus;
      moduleConfig.canWriteOrders = moduleConfig.canWriteOrders ? true : config.canWriteOrders;
      moduleConfig.canReadReceiptOfOrder = moduleConfig.canReadReceiptOfOrder ? true : config.canReadReceiptOfOrder;
      moduleConfig.canWriteReceiptOfOrder = moduleConfig.canWriteReceiptOfOrder ? true : config.canWriteReceiptOfOrder;
      moduleConfig.canSaveForce = moduleConfig.canSaveForce ? true : config.canSaveForce;
      moduleConfig.canReadDebts = moduleConfig.canReadDebts ? true : config.canReadDebts;
      moduleConfig.canWriteDebts = moduleConfig.canWriteDebts ? true : config.canWriteDebts;
    }

    return moduleConfig;
  }

  /**
   * Define and create module access rules using provided portal data.
   */
  @Selector([ProfileState, AccessState])
  static accessToOrders(profileState: ProfileStateModel, accessState: AccessStateModel): OrdersAccessModel {
    const userData = profileState.currentUser;
    const roleList = accessState.roles;

    return OrdersAccessState.generateOrdersAccess(roleList, userData);
  }

}


